import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { ARTICLES, formatDate } from "@/data/articles";

export default function BlogRotator() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % ARTICLES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const article = ARTICLES[index];

  return (
    <section className="py-20 bg-slate-50 border-t border-slate-200" data-testid="blog-rotator">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="font-mono-lbl text-xs text-aix-cyandeep tracking-widest uppercase">04 / DISPATCH</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-2">
              From the Dispatch
            </h2>
          </div>
          <Link
            to="/blog"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-aix-navy hover:text-[#16407F]"
            data-testid="blog-rotator-all-link"
          >
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div
          key={article.slug}
          className="aix-rise border border-slate-200 bg-white shadow-sm rounded-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-5 gap-8 items-center"
          data-testid="blog-rotator-card"
        >
          <div className="md:col-span-3">
          <div className="flex items-center gap-3 font-mono-lbl text-[11px] tracking-widest uppercase mb-4">
            <span className="text-aix-cyandeep bg-sky-50 border border-aix-cyan/30 px-2.5 py-1 rounded">
              {article.category}
            </span>
            <span className="text-slate-400">
              {formatDate(article.date)} · {article.readTime}
            </span>
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4 max-w-3xl" data-testid="blog-rotator-title">
            {article.title}
          </h3>
          <p className="text-slate-500 text-base leading-relaxed max-w-3xl mb-8">{article.excerpt}</p>

          <div className="flex items-center justify-between">
            <Link
              to={`/blog/${article.slug}`}
              className="inline-flex items-center gap-2 bg-aix-navy hover:bg-[#16407F] text-white text-sm font-semibold px-5 py-3 rounded transition-colors"
              data-testid="blog-rotator-read-link"
            >
              Read article <ArrowRight className="w-4 h-4" />
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setIndex((index - 1 + ARTICLES.length) % ARTICLES.length)}
                className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 hover:border-aix-cyan hover:text-aix-navy transition-colors"
                aria-label="Previous article"
                data-testid="blog-rotator-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-2">
                {ARTICLES.map((a, i) => (
                  <button
                    key={a.slug}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to article ${i + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors ${i === index ? "bg-aix-cyan" : "bg-slate-300"}`}
                    data-testid={`blog-rotator-dot-${i}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setIndex((index + 1) % ARTICLES.length)}
                className="w-9 h-9 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 hover:border-aix-cyan hover:text-aix-navy transition-colors"
                aria-label="Next article"
                data-testid="blog-rotator-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          </div>
          <div className="md:col-span-2 h-48 md:h-64 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.imageAlt}
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid="blog-rotator-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
